import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SectionLabel, MaskedLineInView, FadeUp, EASE } from "@/components/Reveal";
import { testimonials } from "@/data/site";

const avatarBg = ["#8C5EBA", "#45A7DE", "#EA5F38"];

const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const t = testimonials[idx];

  const go = (d) => {
    setDir(d);
    setIdx((idx + d + testimonials.length) % testimonials.length);
  };

  return (
    <section data-testid="testimonials-section" className="relative z-10 site-container py-24 sm:py-32">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
        <SectionLabel data-testid="testimonials-label">Growing Together</SectionLabel>
        <h2 className="text-4xl sm:text-6xl lg:text-[68px] font-normal tracking-tight leading-[1.06] lg:text-right lg:max-w-[820px]">
          <MaskedLineInView>Partnerships that last,</MaskedLineInView>
          <MaskedLineInView delay={0.12}>
            <span className="text-[#8C5EBA] font-medium">results that stick.</span>
          </MaskedLineInView>
        </h2>
      </div>

      <FadeUp className="mt-16">
        <div className="border-t border-[#111]/15 pt-12 min-h-[300px] sm:min-h-[260px]">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: dir * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -60 }}
              transition={{ duration: 0.55, ease: EASE }}
            >
              <blockquote
                data-testid="testimonial-quote"
                className="text-2xl sm:text-3xl lg:text-[36px] font-regular tracking-[-0.03em] max-w-5xl"
                style={{ lineHeight: 1.5 }}
              >
                “ {t.quote} ”
              </blockquote>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-between flex-wrap gap-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              className="flex items-center gap-4"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: EASE }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ background: avatarBg[idx % avatarBg.length] }}
              >
                {t.name[0]}
              </div>
              <div>
                <p data-testid="testimonial-name" className="font-bold text-[17px]">{t.name}</p>
                <p data-testid="testimonial-role" className="text-sm text-[#111]/55">{t.role}</p>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center gap-5">
            <button
              data-testid="testimonial-prev-btn"
              onClick={() => go(-1)}
              aria-label="Previous testimonial"
              className="w-12 h-12 rounded-full border border-[#8C5EBA]/50 text-[#8C5EBA] flex items-center justify-center transition-all duration-300 hover:bg-[#8C5EBA] hover:text-white"
            >
              <ArrowLeft size={18} />
            </button>
            <span data-testid="testimonial-counter" className="text-[15px] font-semibold text-[#8C5EBA]">
              {String(idx + 1).padStart(2, "0")}
              <span className="text-[#111]/40">/{String(testimonials.length).padStart(2, "0")}</span>
            </span>
            <button
              data-testid="testimonial-next-btn"
              onClick={() => go(1)}
              aria-label="Next testimonial"
              className="w-12 h-12 rounded-full border border-[#8C5EBA]/50 text-[#8C5EBA] flex items-center justify-center transition-all duration-300 hover:bg-[#8C5EBA] hover:text-white"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </FadeUp>
    </section>
  );
};

export default Testimonials;
